import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Departments } from "../lib/constants";
import { useEmployee, useJob } from "../services";

export const AnalystJobViewContext = createContext<any>(null);

export const AnalystJobViewProvider = ({ children }) => {
  const { id } = useParams();
  const { getJobDetailsById, markMilestone, assignWorkToEmployee } = useJob();

  const [loadStatus, setLoadStatus] = useState({ loading: false, error: null });
  const [jobData, setJobData] = useState<Record<string, any>>({}); // typed as any for dynamic API response
  const [params, setParams] = useSearchParams();

  console.log({ jobData });

  const commonJobDetails = useMemo(
    () => ({
      jobId: jobData.job_id,
      jobCode: jobData.job_code,
      jobName: jobData.job_name,
      jobStatus: jobData.job_status,
      jobValue: jobData.job_value,
      milestoneCount: jobData.milestone_count,
      milestonesCompleted: jobData.milestones_completed ?? 0,
      jobDate: jobData.job_date,
      clientName: jobData.client?.name,
    }),
    [
      jobData.job_id,
      jobData.job_code,
      jobData.job_name,
      jobData.job_status,
      jobData.job_value,
      jobData.milestone_count,
      jobData.milestones_completed,
      jobData.job_date,
      jobData.client,
    ]
  );

  const milestoneCount = useMemo(() => commonJobDetails.milestoneCount, [commonJobDetails.milestoneCount]);
  const milestonesCompleted = useMemo(
    () => commonJobDetails.milestonesCompleted,
    [commonJobDetails.milestonesCompleted]
  );

  const milestones = useMemo(() => jobData.milestones || [], [jobData.milestones]);

  const basicDetails = useMemo(
    () => ({
      jobName: jobData.job_name,
      jobDate: jobData.job_date,
      siteAddress: jobData.site_address,
      locality: jobData.locality,
    }),
    [jobData.job_date, jobData.job_name, jobData.locality, jobData.site_address]
  );

  const jobType = useMemo(
    () => (jobData.type_of_work?.job_type === "New Job" ? "New Work" : "Existing Work"),
    [jobData]
  );
  const jobWorkType = useMemo(() => jobData.type_of_work?.job_work_type, [jobData]);
  const isNewWork = useMemo(() => jobType === "New Work", [jobType]);
  const isExistingWork = useMemo(() => jobType === "Existing Work", [jobType]);
  const isAlliedWork = useMemo(() => jobWorkType === "Allied Work", [jobWorkType]);
  const isBuildingWork = useMemo(() => jobWorkType === "Building Work", [jobWorkType]);

  const columnDrawingString = useMemo(
    () => (isAlliedWork || isNewWork ? `C1 - C${jobData.type_of_work?.column_drawings_count}` : null),
    [isAlliedWork, isNewWork, jobData.type_of_work?.column_drawings_count]
  );
  const slabsCountString = useMemo(() => {
    const { basement_count, parking_count, ground_floor, upper_floor_count, existing_floor_count } =
      jobData.type_of_work || {};
    const basementDrawings = basement_count;
    const parkingDrawings = parking_count;
    const groundFloorDrawings = ground_floor === "None" ? "0" : ground_floor === "Ground Level" ? "1" : 2;
    const upperFloorDrawings = upper_floor_count;
    const existingFloorDrawings = existing_floor_count;

    const slabsCount = jobData.type_of_work?.structural_drawings_count - jobData.type_of_work?.column_drawings_count;
    if (slabsCount === 0) return "No slabs";
    if (isNewWork || isAlliedWork) return slabsCount === 1 ? "S1" : `S1 - S${slabsCount}`;
    const totalDrawings =
      2 + basementDrawings + parkingDrawings + groundFloorDrawings + upperFloorDrawings + existingFloorDrawings;
    const newDrawings = upperFloorDrawings - existingFloorDrawings + 2;
    const start = totalDrawings - newDrawings + 1;

    return slabsCount === 1 ? `S${start}` : `S${start} - S${totalDrawings}`;
  }, [isAlliedWork, isNewWork, jobData.type_of_work]);

  const typeOfWork = useMemo(() => {
    const typeOfWorkObj = jobData.type_of_work || {};
    let data: Record<string, any> = {
      jobType,
      isAlliedWork,
      isBuildingWork,
      columnDrawings: columnDrawingString,
      slabsCount: slabsCountString,
    };

    if (isAlliedWork) return { ...data, alliedWorkSpecification: typeOfWorkObj.allied_work_details };

    data = {
      ...data,
      // buildingType: typeOfWorkObj.building_type, // removed - not in state type
      // basementCount and parkingCount removed - not in typeOfWork state type
      // parkingCount: typeOfWorkObj.parking_count, // not in state type
      groundFloor: typeOfWorkObj.ground_floor,
      upperFloorCount: typeOfWorkObj.upper_floor_count,
      isExistingWork,
    };
    if (isExistingWork) {
      return { ...data, existingFloorCount: typeOfWorkObj.existing_floor_count };
    } else return { ...data, hasAdditionalFloor: typeOfWorkObj.additional_floor_count > 0 };
  }, [
    columnDrawingString,
    isAlliedWork,
    isBuildingWork,
    isExistingWork,
    jobData.type_of_work,
    jobType,
    slabsCountString,
  ]);

  const poc = useMemo(
    () => ({
      client: jobData.client || {},
      architect: jobData.architect || {},
      builder: jobData.builder || {},
      owner: { ...(jobData.owner || {}), locality: jobData.locality, site_address: jobData.site_address },
      sanctioningAuthority: jobData.sanctioning_authority || "",
    }),
    [
      jobData.architect,
      jobData.builder,
      jobData.client,
      jobData.locality,
      jobData.owner,
      jobData.sanctioning_authority,
      jobData.site_address,
    ]
  );

  const pocNames = useMemo(
    () => ({
      client: poc.client.name,
      architect: poc.architect.name,
      builder: poc.builder.name,
      owner: poc.owner.name,
    }),
    [poc]
  );

  const files = useMemo(() => {
    const inwardDocuments = jobData.files?.filter((file) => file.category === "inward_documents") || [];
    const outwardDocuments = jobData.files?.filter((file) => file.category === "outward_documents") || [];
    const financialDocuments = jobData.files?.filter((file) => file.category === "financial_documents") || [];

    return { inwardDocuments, outwardDocuments, financialDocuments };
  }, [jobData.files]);

  const quotationDocument = useMemo(
    () => files.financialDocuments.find((file) => file.name.includes("Quotation")) || null,
    [files.financialDocuments]
  );
  const isQuotationFrozen = useMemo(() => quotationDocument?.isFrozen || false, [quotationDocument]);

  const appointmentLetter = useMemo(() => files.inwardDocuments.appointment_letter || null, [files.inwardDocuments]);
  const acceptanceLetterA = useMemo(() => files.outwardDocuments.acceptance_a_letter || null, [files.outwardDocuments]);
  const acceptanceLetterB = useMemo(() => files.outwardDocuments.acceptance_b_letter || null, [files.outwardDocuments]);

  const isAddJobComplete = useMemo(() => {
    const { owner } = jobData;
    if (!owner?.sanction_date || !owner?.sanction_number) return false;

    if (!appointmentLetter || !acceptanceLetterA || !acceptanceLetterB) return false;
    if (!isQuotationFrozen) return false;

    return true;
  }, [acceptanceLetterA, acceptanceLetterB, appointmentLetter, isQuotationFrozen, jobData]);

  const createTaskListFromPrefix = (prefix, start, end) =>
    Array.from({ length: end - start + 1 }, (_, i) => {
      const index = i + start;
      return [
        `${prefix}${index}.1 Drawing`,
        `${prefix}${index}.2 Drawing`,
        `${prefix}${index}.1 Design`,
        `${prefix}${index}.2 Design`,
      ];
    }).flat();

  const allTaskList = useMemo(() => {
    let columnTasks = [];
    let slabsTasks = [];
    const slabsArr = slabsCountString.replaceAll("S", "").split(" - ");
    if (isAlliedWork || isNewWork) {
      const columnArr = columnDrawingString.replaceAll("C", "").split(" - ");

      if (columnArr.length === 1)
        columnTasks = createTaskListFromPrefix("C", Number(columnArr[0]), Number(columnArr[0]));
      else columnTasks = createTaskListFromPrefix("C", Number(columnArr[0]), Number(columnArr[1]));
    }
    if (slabsArr.length === 1) slabsTasks = createTaskListFromPrefix("S", Number(slabsArr[0]), Number(slabsArr[0]));
    else slabsTasks = createTaskListFromPrefix("S", Number(slabsArr[0]), Number(slabsArr[1]));

    return [...columnTasks, ...slabsTasks];
  }, [columnDrawingString, isAlliedWork, isNewWork, slabsCountString]);

  const assignedTasks = useMemo(() => jobData.assigned_tasks ?? [], [jobData.assigned_tasks]);

  const assignedTasksNames = useMemo(() => assignedTasks.map((task) => task.task_name), [assignedTasks]);

  const tasksInProgress = useMemo(
    () =>
      assignedTasks
        .filter((task) => task.submissions?.length > 0 && task.status !== "Approved")
        .map((task) => ({
          ...task,
          submissions: task.submissions.map((submission) => ({
            ...submission,
            submissionStatus: submission.revision_number < task.submissions.length ? "Revision Requested" : task.status,
          })),
        })),
    [assignedTasks]
  );

  const approvedTasks = useMemo(
    () =>
      assignedTasks
        .filter((task) => task.status === "Approved")
        .map((task) => ({
          ...task,
          submissions: task.submissions.map((submission) => ({
            ...submission,
            submissionStatus: submission.revision_number < task.submissions.length ? "Revision Requested" : task.status,
          })),
        })),
    [assignedTasks]
  );

  const assignedSiteVisits = useMemo(() => {
    const scheduledVisits = jobData.assigned_site_visits?.filter((visit) => visit.status !== "Completed") ?? [];
    const completedVisits = jobData.assigned_site_visits?.filter((visit) => visit.status === "Completed") ?? [];

    return [...scheduledVisits, ...completedVisits];
  }, [jobData.assigned_site_visits]);

  const maxFreeVisitCount = useMemo(
    () => jobData.site_engineer_free_visit_count,
    [jobData.site_engineer_free_visit_count]
  );

  const addJobSteps = useMemo(
    () => [
      { label: "Basic Details", handle: "basic_details", status: "completed" },
      { label: "Type of Work", handle: "type_of_work", status: "completed" },
      {
        label: "POC",
        handle: "poc",
        status: poc.owner.sanction_date && poc.owner.sanction_number ? "completed" : "active",
      },
      { label: "Accounting", handle: "accounting", status: "completed" },
      {
        label: "Quotation",
        handle: "quotation",
        status: quotationDocument ? (isQuotationFrozen ? "completed" : "active") : "",
      },
      { label: "File Section", handle: "file_section", status: "active" },
    ],
    [isQuotationFrozen, poc.owner.sanction_date, poc.owner.sanction_number, quotationDocument]
  );

  const jobViewSteps = useMemo(
    () => [
      {
        label: "New Job",
        handle: "new_job",
        status: isAddJobComplete ? "completed" : "active",
        disabled: isAddJobComplete,
      },
      {
        label: "Quotation approval",
        handle: "quotation_approval",
        status: quotationDocument ? (isQuotationFrozen ? "completed" : "active") : "",
        disabled: true,
      },
      {
        label: "Milestones",
        handle: "milestones",
        status: milestonesCompleted === milestoneCount ? "completed" : milestonesCompleted > 0 ? "active" : "",
        disabled: false,
      },
      {
        label: "Work Progress",
        handle: "work_progress",
        status: approvedTasks.length === allTaskList.length ? "completed" : assignedTasks.length > 0 ? "active" : "",
        disabled: false,
      },
      {
        label: "Site Visit",
        handle: "site_visit",
        status: assignedSiteVisits.length > 0 ? "active" : "",
        disabled: false,
      },
      {
        label: "Certificates",
        handle: "certificates",
        status:
          files.inwardDocuments.length > 0 || files.outwardDocuments.length > 0 || files.financialDocuments.length > 0
            ? "active"
            : "",
        disabled: false,
      },
      { label: "Completed", handle: "completed", status: "", disabled: true }, // to change later
    ],
    [
      allTaskList.length,
      approvedTasks.length,
      assignedSiteVisits.length,
      assignedTasks.length,
      files.financialDocuments.length,
      files.inwardDocuments.length,
      files.outwardDocuments.length,
      isAddJobComplete,
      isQuotationFrozen,
      milestoneCount,
      milestonesCompleted,
      quotationDocument,
    ]
  );

  const workProgressSteps = useMemo(
    () => [
      {
        label: "Assign Work",
        handle: "assign",
        status: assignedTasks.length === allTaskList.length ? "completed" : assignedTasks.length > 0 ? "active" : "",
        disabled: false,
      },
      {
        label: "In Progress",
        handle: "progress",
        status: tasksInProgress.length > 0 ? "active" : "",
        disabled: tasksInProgress.length === 0,
      },
      {
        label: "Work Approval",
        handle: "approval",
        status: approvedTasks.length === allTaskList.length ? "completed" : approvedTasks.length > 0 ? "active" : "",
        disabled: approvedTasks.length === 0,
      },
    ],
    [allTaskList.length, approvedTasks.length, assignedTasks.length, tasksInProgress.length]
  );

  const [currentStep, setCurrentStep] = useState({ index: 0, handle: jobViewSteps[0].handle });
  const [currentWorkStep, setCurrentWorkStep] = useState({ index: 0, handle: workProgressSteps[0].handle });

  const handleStepChange = (index) => {
    const totalSteps = jobViewSteps.length;
    const newIndex = index < 0 ? 0 : index >= totalSteps - 1 ? totalSteps - 2 : index;
    setCurrentStep({ index: newIndex, handle: jobViewSteps[newIndex].handle });

    if (jobViewSteps[newIndex].handle === "work_progress") {
      const workParams = params.get("work") ?? "assign";
      const currentWorkProgressStep = workProgressSteps.find((step) => step.handle === workParams);
      setParams({ stage: jobViewSteps[newIndex].handle, work: currentWorkProgressStep?.handle ?? "assign" });
      setCurrentWorkStep({
        index: workProgressSteps.findIndex((step) => step.handle === workParams),
        handle: currentWorkProgressStep?.handle ?? "assign",
      });
    } else {
      setParams({ stage: jobViewSteps[newIndex].handle });
      setCurrentStep({
        index: jobViewSteps.findIndex((step) => step.handle === jobViewSteps[newIndex].handle),
        handle: jobViewSteps[newIndex].handle,
      });
    }
  };

  const handleWorkProgressStepChange = (index) => {
    const totalSteps = workProgressSteps.length;
    const newIndex = index < 0 ? 0 : index >= totalSteps - 1 ? totalSteps - 1 : index;
    setCurrentWorkStep({ index: newIndex, handle: workProgressSteps[newIndex].handle });
    setParams({ stage: "work_progress", work: workProgressSteps[newIndex].handle });
  };

  const fetchJobDetails = useCallback(async () => {
    setLoadStatus({ loading: true, error: null });
    const { job, error } = await getJobDetailsById(id);
    if (error) {
      setLoadStatus({ loading: false, error });
    } else {
      setLoadStatus({ loading: false, error });

      const stageParams = params.get("stage") ?? "new_job";
      if (stageParams === "work_progress") {
        const workParams = params.get("work") ?? "assign";
        const currentWorkProgressStep = workProgressSteps.find((step) => step.handle === workParams);
        setParams({ stage: stageParams, work: currentWorkProgressStep?.handle ?? "assign" });
        setCurrentWorkStep({
          index: workProgressSteps.findIndex((step) => step.handle === workParams),
          handle: currentWorkProgressStep?.handle ?? "assign",
        });
      } else {
        setParams({ stage: stageParams });
        setCurrentStep({ index: jobViewSteps.findIndex((step) => step.handle === stageParams), handle: stageParams });
      }
      setJobData(job);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getJobDetailsById]);

  const onJobStatusUpdate = (data) => {
    console.log(data);
  };

  const { getAllEmployeesForWorkAllotment } = useEmployee();
  const [employeeList, setEmployeeList] = useState([]);

  const getEmployeeList = useCallback(async () => {
    const { employees, error } = await getAllEmployeesForWorkAllotment();

    console.log(employees, error);
    if (error) {
      toast.error(error || "Failed to fetch employees");
      setEmployeeList([]);
    }
    setEmployeeList(employees);
  }, [getAllEmployeesForWorkAllotment]);

  const siteVisitEmployees = useMemo(
    () => employeeList?.filter((emp) => emp.department === Departments.SITE_VISIT),
    [employeeList]
  );

  const drawingEmployees = useMemo(
    () => employeeList?.filter((emp) => emp.department === Departments.DRAWING),
    [employeeList]
  );

  const designEmployees = useMemo(
    () => employeeList?.filter((emp) => emp.department === Departments.DESIGN),
    [employeeList]
  );

  const [assignNewSiteVisit, setAssignNewSiteVisit] = useState(false);
  const handleAssignNewSiteVisitClick = () => {
    setAssignNewSiteVisit(true);
    handleStepChange(jobViewSteps.findIndex((step) => step.handle === "site_visit"));
  };

  const siteVisitDetails = useMemo(
    () => ({
      jobCode: commonJobDetails?.jobCode,
      jobName: commonJobDetails?.jobName,
      employees: siteVisitEmployees,
      siteAddress: basicDetails?.siteAddress,
      supervisorName: jobData?.owner?.supervisor_name,
      supervisorContact: jobData?.owner?.supervisor_contact,
      inwardDocuments: files.inwardDocuments,
      outwardDocuments: files.outwardDocuments,
    }),
    [
      basicDetails?.siteAddress,
      commonJobDetails?.jobCode,
      commonJobDetails?.jobName,
      files.inwardDocuments,
      files.outwardDocuments,
      jobData?.owner?.supervisor_contact,
      jobData?.owner?.supervisor_name,
      siteVisitEmployees,
    ]
  );

  const updateMilestoneStatus = async (milestone) => {
    const jobId = id;
    if (!jobId) return milestone;
    const status = milestone.status;
    if (!["not_started", "milestone_achieved"].includes(status)) return milestone;
    const milestone_number = milestone.milestone_number;
    const newStatus = status === "not_started" ? "milestone_achieved" : "not_started";

    const { milestone: updatedMilestone, error } = await markMilestone(jobId, {
      job_id: jobId,
      milestone_number,
      status: newStatus,
    });
    if (error) {
      toast.error(error);
      return milestone;
    } else {
      fetchJobDetails();
      return jobData.milestones.find((milestone) => milestone.milestone_number === updatedMilestone.milestone_number);
    }
  };

  const assignTaskToEmployee = async (assignment) => {
    const jobId = id;
    if (!jobId) return;
    const { error, message } = await assignWorkToEmployee(assignment, jobId);
    if (error) {
      toast.error(error);
      return;
    } else {
      toast.success(message)
      fetchJobDetails();
    }
  };

  useEffect(() => {
    fetchJobDetails();
  }, [fetchJobDetails]);

  useEffect(() => {
    const stageParams = params.get("stage") ?? "new_job";
    setParams({ stage: stageParams });
    setCurrentStep({ index: jobViewSteps.findIndex((step) => step.handle === stageParams), handle: stageParams });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getEmployeeList();
  }, [getEmployeeList]);

  return (
    <AnalystJobViewContext.Provider
      value={{
        loadStatus,
        commonJobDetails,
        basicDetails,
        typeOfWork,
        poc,
        files,
        jobViewSteps,
        currentStep,
        milestoneCount,
        milestonesCompleted,
        milestones,
        workProgressSteps,
        currentWorkStep,
        jobData,
        columnDrawingString,
        slabsCountString,
        allTaskList,
        assignedTasks,
        assignedTasksNames,
        pocNames,
        tasksInProgress,
        approvedTasks,
        assignedSiteVisits,
        maxFreeVisitCount,
        addJobSteps,
        employeeList,
        siteVisitEmployees,
        drawingEmployees,
        designEmployees,
        siteVisitDetails,
        assignNewSiteVisit,
        handleAssignNewSiteVisitClick,
        handleStepChange,
        onJobStatusUpdate,
        handleWorkProgressStepChange,
        updateMilestoneStatus,
        assignTaskToEmployee,
      }}
    >
      {children}
    </AnalystJobViewContext.Provider>
  );
};

export const useAnalystJobView = () => {
  const context = useContext(AnalystJobViewContext);
  if (!context) {
    throw new Error("useAnalystJobView must be used within a AnalystJobViewProvider");
  }
  return context;
};
